import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  IdentificationIcon,
  AcademicCapIcon,
  DocumentDuplicateIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  SparklesIcon,
  CheckIcon,
  XCircleIcon,
  ArrowUturnLeftIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../lib/axiosConfig';
import { nameMatchSummary } from '../../lib/documentStatus';
import BankDetailsPanel from './BankDetailsPanel';
import FieldEditHistory from '../ui/FieldEditHistory';

function formatDate(d) {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/** Pick an icon based on document type/label keywords and value */
function getDocIcon(label = '', type = '', docNum = '') {
  const cleanDoc = String(docNum || '').replace(/\s+/g, '').toUpperCase();
  if (/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanDoc)) return CreditCardIcon;
  if (/^\d{12}$/.test(cleanDoc)) return IdentificationIcon;

  const s = `${label} ${type}`.toLowerCase();
  if (s.includes('aadhaar') || s.includes('aadhar')) return IdentificationIcon;
  if (s.includes('pan')) return CreditCardIcon;
  if (s.includes('passport')) return ShieldCheckIcon;
  if (s.includes('degree') || s.includes('certificate') || s.includes('marksheet') || s.includes('academic'))
    return AcademicCapIcon;
  if (s.includes('license') || s.includes('licence')) return IdentificationIcon;
  return DocumentDuplicateIcon;
}

/**
 * Submissions that are a declaration rather than a file — nothing to preview or download.
 *
 * `hasFile` is the authoritative signal (sent by the server); the subtype list is the
 * fallback for payloads and stored rows written before that field existed.
 */
function isDeclarationOnly(requested = {}, uploaded = null) {
  if (uploaded && typeof uploaded.hasFile === 'boolean') return !uploaded.hasFile;
  return ['uan_number', 'complete_address', 'marital_status'].includes(requested?.subtype);
}

/** Label for the document number field based on type and value */
function getIdLabel(label = '', type = '', docNum = '', kind = '') {
  // A PAN acknowledgement is not a PAN — without this the 15-digit receipt number falls
  // through to the "pan" keyword below and is labelled as the PAN itself.
  if (kind === 'pan_acknowledgement') return 'PAN Acknowledgement No.';
  // Likewise a bank account number, which is 9-18 digits — a 12-digit one would
  // otherwise fall through to the Aadhaar rule below and be labelled as an Aadhaar.
  if (kind === 'bank_account_number') return 'Bank Account No.';
  const cleanDoc = String(docNum || '').replace(/\s+/g, '').toUpperCase();
  if (/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanDoc)) return 'PAN Number';
  if (/^\d{12}$/.test(cleanDoc)) return 'Aadhaar Number';

  const s = `${label} ${type}`.toLowerCase();
  if (s.includes('uan')) return 'UAN Number';
  if (s.includes('aadhaar') || s.includes('aadhar')) return 'Aadhaar Number';
  if (s.includes('pan')) return 'PAN Number';
  if (s.includes('passport')) return 'Passport Number';
  if (s.includes('driving') || s.includes('license') || s.includes('licence')) return 'Licence Number';
  if (s.includes('voter')) return 'Voter ID';
  if (s.includes('gstin') || s.includes('gst')) return 'GSTIN';
  return 'Document Number';
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy to clipboard"
      className="ml-1.5 inline-flex items-center justify-center rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
    >
      {copied ? (
        <CheckCircleIcon className="h-3.5 w-3.5 text-emerald-500" />
      ) : (
        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

const EMPTY_REJECTIONS = { pending: [], awaiting: [] };

export default function DocumentViewerModal({ isOpen, onClose, candidate, documentData, screeningId, onDocumentsChanged }) {
  const [activeTabKey, setActiveTabKey] = useState(null);
  const [previewState, setPreviewState] = useState({}); // { [key]: { url, fileName, label, loading, error } }
  const [downloading, setDownloading] = useState(false);

  // HR "Reject document" (Go Colors only — the server sends rejectionsEnabled). A Reject only
  // marks the document; one email goes to the candidate on Send (or the server's auto-send).
  const rejectionsEnabled = documentData?.rejectionsEnabled === true;
  const [rejections, setRejections] = useState(EMPTY_REJECTIONS);
  const [rejectBusy, setRejectBusy] = useState(false);
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const [sentDialogOpen, setSentDialogOpen] = useState(false);
  useEffect(() => {
    setRejections(documentData?.rejections || EMPTY_REJECTIONS);
  }, [documentData]);
  const pendingByKey = useMemo(
    () => new Map((rejections.pending || []).map((p) => [`${p.type}::${p.subtype}`, p])),
    [rejections]
  );
  const awaitingByKey = useMemo(
    () => new Map((rejections.awaiting || []).map((a) => [`${a.type}::${a.subtype}`, a])),
    [rejections]
  );
  const pendingCount = (rejections.pending || []).length;
  const emailFailed = (rejections.awaiting || []).some((a) => a.emailFailed);
  const candidateId = candidate?._id || candidate?.id;
  const rejectionsUrl = `/api/documents/screening/${screeningId}/candidates/${candidateId}/rejections`;

  const callRejections = async (request) => {
    if (rejectBusy) return null;
    setRejectBusy(true);
    try {
      const res = await request();
      if (res.data?.rejections) setRejections(res.data.rejections);
      // The parent's copy feeds the next open of this modal.
      onDocumentsChanged?.({ candidates: false });
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
      return null;
    } finally {
      setRejectBusy(false);
    }
  };
  const rejectDocument = (doc, reason) =>
    callRejections(() => axios.post(rejectionsUrl, { type: doc.type, subtype: doc.subtype, reason }));
  const undoReject = (doc) =>
    callRejections(() => axios.delete(rejectionsUrl, { params: { type: doc.type, subtype: doc.subtype } }));
  const discardRejections = () => callRejections(() => axios.delete(rejectionsUrl));
  const sendRejections = async ({ closing = false } = {}) => {
    const data = await callRejections(() => axios.post(`${rejectionsUrl}/send`));
    if (!data) return false;
    if (data.warning) toast.error(data.warning);
    else if (closing) toast.success('Email sent to the candidate to re-upload the rejected documents');
    else setSentDialogOpen(true);
    onDocumentsChanged?.({ candidates: true });
    return true;
  };

  // Closing with unsent rejections asks first: they would otherwise go out on their own later.
  const requestClose = () => {
    if (pendingCount > 0) setClosePromptOpen(true);
    else onClose();
  };

  // Go Colors: HR can correct the Aadhaar / PAN number and the bank fields (server sends editKind
  // per document). A saved edit shows at once from the server's reply; the parent's refetch follows.
  const fieldEditsEnabled = documentData?.fieldEditsEnabled === true;
  const [savedEdits, setSavedEdits] = useState({});
  useEffect(() => {
    setSavedEdits({});
  }, [documentData]);
  const saveFields = async (doc, body) => {
    try {
      const res = await axios.patch(`/api/documents/submissions/${doc.id}/fields`, body);
      const saved = res.data?.document;
      if (saved) setSavedEdits((prev) => ({ ...prev, [String(saved.id)]: saved }));
      toast.success('Details updated');
      onDocumentsChanged?.({ candidates: false });
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save the change. Please try again.');
      return false;
    }
  };

  const uploadedMap = useMemo(() => {
    const m = new Map();
    // Newest first from the server: keep the first per document, the one the Excel export uses.
    (documentData?.documents || []).forEach((d) => {
      const key = `${d.type}::${d.subtype}`;
      if (!m.has(key)) m.set(key, savedEdits[String(d.id)] || d);
    });
    return m;
  }, [documentData, savedEdits]);

  const requested = documentData?.requestedDocuments || [];
  const status = documentData?.status;
  const submittedAt = documentData?.submittedAt;

  // Build tab list: each requested doc becomes a tab
  const tabs = useMemo(() => {
    return requested.map((r) => {
      const key = `${r.type}::${r.subtype}`;
      const uploaded = uploadedMap.get(key);
      return { key, r, uploaded, done: !!uploaded };
    });
  }, [requested, uploadedMap]);

  // Set first tab as active when modal opens / data changes
  useEffect(() => {
    if (isOpen && tabs.length > 0) {
      setActiveTabKey(tabs[0].key);
    }
    if (!isOpen) {
      setActiveTabKey(null);
      setPreviewState({});
    }
  }, [isOpen, tabs.length]);

  const activeTab = tabs.find((t) => t.key === activeTabKey) || null;

  // Fetch preview URL for a given submission id, keyed by tab key
  const loadPreview = useCallback(async (tabKey, submissionId) => {
    if (!submissionId) return;
    setPreviewState((prev) => ({ ...prev, [tabKey]: { loading: true, url: null, fileName: '', label: '', error: null } }));
    try {
      const res = await axios.get(`/api/documents/view/${submissionId}`);
      const url = res.data?.url;
      if (!url) throw new Error('Missing URL');
      setPreviewState((prev) => ({
        ...prev,
        [tabKey]: { loading: false, url, fileName: res.data?.fileName || '', label: res.data?.label || '', error: null },
      }));
    } catch (err) {
      setPreviewState((prev) => ({
        ...prev,
        [tabKey]: { loading: false, url: null, fileName: '', label: '', error: err?.response?.data?.message || 'Failed to load preview' },
      }));
      toast.error(err?.response?.data?.message || 'Failed to load preview');
    }
  }, []);

  // Auto-load preview when switching to an uploaded tab (skip UAN — no file to preview)
  useEffect(() => {
    if (!activeTab) return;
    if (!activeTab.done) return;
    if (isDeclarationOnly(activeTab.r, activeTab.uploaded)) return; // Declaration, no file to preview
    const existingState = previewState[activeTab.key];
    if (existingState?.url || existingState?.loading) return; // already loaded or loading
    loadPreview(activeTab.key, activeTab.uploaded?.id);
  }, [activeTabKey, activeTab, loadPreview]);

  const handleDownload = async (tabKey, submissionId, fallbackFileName, fallbackLabel) => {
    if (!submissionId || downloading) return;
    try {
      setDownloading(true);
      const loadingId = toast.loading('Downloading document…');
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/api/documents/download/${submissionId}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) throw new Error(`Failed to download document (${resp.status})`);
      const contentType = (resp.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('text/html')) throw new Error('Unexpected HTML response while downloading document');
      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition') || '';
      const dispositionMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
      const decodedName = dispositionMatch?.[1] ? decodeURIComponent(dispositionMatch[1]) : '';
      const fallback = (fallbackFileName || fallbackLabel || 'document').toString().trim() || 'document';
      const fallbackFilename = fallback.includes('.') ? fallback : `${fallback}.pdf`;
      const filename = decodedName.trim() || fallbackFilename;
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.dismiss(loadingId);
      toast.success('Document downloaded');
    } catch {
      toast.error('Failed to download document');
    } finally {
      window.setTimeout(() => setDownloading(false), 700);
    }
  };

  const getPreviewKind = (fileName = '', url = '') => {
    const hay = `${fileName} ${url}`.toLowerCase();
    if (hay.includes('.png') || hay.includes('.jpg') || hay.includes('.jpeg') || hay.includes('.webp')) return 'image';
    if (hay.includes('.pdf')) return 'pdf';
    return 'unknown';
  };

  const name = candidate?.name || 'Candidate';

  return (
    <>
    <AnimatedModal
      isOpen={isOpen}
      onClose={requestClose}
      ariaLabelledBy="doc-viewer-title"
      backdropClassName="bg-black/50"
      contentClassName="w-full max-w-7xl mx-auto flex h-[90vh] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      zIndex={50}
      overflowHidden={true}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 bg-gradient-to-br from-[#007BFF]/5 via-white to-slate-50/80 px-6 pt-5 pb-4">
          <div className="min-w-0 flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#007BFF]/10 text-[#007BFF]">
              <DocumentTextIcon className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h3 id="doc-viewer-title" className="text-base font-semibold tracking-tight text-gray-900">
                Candidate Documents
              </h3>
              <p className="mt-0.5 text-sm text-slate-600">
                <span className="font-medium text-slate-800">{name}</span>
                <span className="mx-1.5 text-slate-300">·</span>
                {(rejections.awaiting || []).length > 0 ? (
                  <span className="text-amber-700 font-medium">Re-upload pending from candidate</span>
                ) : status === 'submitted' ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                    <CheckCircleIcon className="h-3.5 w-3.5" /> All documents received
                  </span>
                ) : status === 'partial' ? (
                  <span className="text-amber-700 font-medium">Some documents pending</span>
                ) : (
                  <span className="text-slate-500">Upload status unknown</span>
                )}
              </p>
              {submittedAt && (
                <p className="mt-1 text-[11px] text-slate-400">
                  Submitted {formatDate(submittedAt)}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* ── Empty state ── */}
        {tabs.length === 0 && (
          <div className="flex flex-1 items-center justify-center px-6 py-16">
            <div className="text-center">
              <DocumentDuplicateIcon className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">No document requests configured</p>
              <p className="mt-1 text-xs text-slate-400">No documents were requested for this candidate.</p>
            </div>
          </div>
        )}

        {tabs.length > 0 && (
          <div className="flex min-h-0 flex-1 overflow-hidden">

            {/* ── Left sidebar tabs — no scroll, fits within modal ── */}
            <div className="flex w-56 shrink-0 flex-col border-r border-gray-100 bg-slate-50/70">
              <div className="px-3 pt-3 pb-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-1">
                  {tabs.length} Document{tabs.length !== 1 ? 's' : ''}
                </p>
              </div>
              <nav className="flex flex-col gap-1 px-2 pb-3">
                {tabs.map(({ key, r, uploaded, done }) => {
                  const isActive = key === activeTabKey;
                  const Icon = getDocIcon(r.label, r.type, uploaded?.documentNumber);
                  const marked = pendingByKey.has(key);
                  const awaiting = !done && awaitingByKey.has(key);
                  const subtitle = marked ? 'Rejected · not sent' : awaiting ? 'Re-upload requested' : done ? 'Uploaded' : 'Pending';
                  const subtitleColor = marked ? 'text-red-600' : done ? 'text-emerald-600' : 'text-amber-600';
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTabKey(key)}
                      className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all ${
                        isActive
                          ? 'bg-[#007BFF] text-white shadow-md shadow-[#007BFF]/20'
                          : 'text-slate-700 hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-white/90' : marked ? 'text-red-500' : done ? 'text-emerald-500' : 'text-amber-400'}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-semibold leading-snug truncate ${isActive ? 'text-white' : 'text-slate-800'}`}>
                          {r.label}
                        </p>
                        <p className={`text-[10px] leading-snug mt-0.5 ${isActive ? 'text-white/70' : subtitleColor}`}>
                          {subtitle}
                        </p>
                      </div>
                      {done && !isActive && (
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${marked ? 'bg-red-400' : 'bg-emerald-400'}`} />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* ── Right content panel ── */}
            {activeTab && (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
                {activeTab.done ? (
                  <UploadedDocPanel
                    // Per document: an open edit form must never carry over to another tab.
                    key={activeTab.key}
                    tab={activeTab}
                    previewState={previewState[activeTab.key] || {}}
                    onDownload={handleDownload}
                    downloading={downloading}
                    getPreviewKind={getPreviewKind}
                    onRetry={() => loadPreview(activeTab.key, activeTab.uploaded?.id)}
                    onSaveFields={
                      fieldEditsEnabled && activeTab.uploaded?.editKind
                        ? (body) => saveFields(activeTab.uploaded, body)
                        : null
                    }
                    rejection={
                      rejectionsEnabled
                        ? {
                            marked: pendingByKey.get(activeTab.key) || null,
                            busy: rejectBusy,
                            onReject: (reason) => rejectDocument(activeTab.r, reason),
                            onUndo: () => undoReject(activeTab.r),
                          }
                        : null
                    }
                  />
                ) : (
                  <PendingDocPanel
                    tab={activeTab}
                    docStatus={status}
                    candidate={candidate}
                    reupload={awaitingByKey.get(activeTab.key) || null}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Unsent rejections: one email for all of them ── */}
        {rejectionsEnabled && (pendingCount > 0 || emailFailed) && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-6 py-3 shadow-[0_-4px_12px_-6px_rgba(15,23,42,0.12)]">
            <p className="flex items-center gap-2 text-sm text-slate-700">
              <XCircleIcon className="h-5 w-5 text-red-500 shrink-0" />
              {pendingCount > 0 ? (
                <span>
                  <span className="font-semibold">{pendingCount} document{pendingCount !== 1 ? 's' : ''}</span> marked for re-upload
                  <span className="text-slate-400"> · the candidate has not been notified yet</span>
                </span>
              ) : (
                <span>The re-upload email could not be delivered to the candidate.</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {pendingCount > 0 && (
                <button
                  type="button"
                  onClick={discardRejections}
                  disabled={rejectBusy}
                  className="rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Discard
                </button>
              )}
              <button
                type="button"
                onClick={() => sendRejections()}
                disabled={rejectBusy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
              >
                <PaperAirplaneIcon className="h-3.5 w-3.5" />
                {rejectBusy ? 'Sending…' : pendingCount > 0 ? 'Send to candidate' : 'Resend email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </AnimatedModal>

      <RejectionDialog
        isOpen={closePromptOpen}
        onDismiss={() => setClosePromptOpen(false)}
        icon={<ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />}
        iconBg="bg-amber-100"
        title="Send rejection email to the candidate?"
        message={`You marked ${pendingCount} document${pendingCount !== 1 ? 's' : ''} for re-upload, but the candidate has not been notified yet.`}
        secondaryLabel="Discard"
        onSecondary={async () => {
          if (await discardRejections()) {
            setClosePromptOpen(false);
            onClose();
          }
        }}
        primaryLabel="Send email"
        onPrimary={async () => {
          if (await sendRejections({ closing: true })) {
            setClosePromptOpen(false);
            onClose();
          }
        }}
        busy={rejectBusy}
      />
      <RejectionDialog
        isOpen={sentDialogOpen}
        onDismiss={() => setSentDialogOpen(false)}
        icon={<CheckCircleIcon className="h-5 w-5 text-emerald-600" />}
        iconBg="bg-emerald-100"
        title="Email sent"
        message="The candidate has been emailed to re-upload the rejected documents. You will be notified once they upload them."
        primaryLabel="OK"
        onPrimary={() => setSentDialogOpen(false)}
      />
    </>
  );
}

/* ─────────── Small dialog for the reject flow (backdrop = stay, never a choice) ─────────── */
function RejectionDialog({ isOpen, onDismiss, icon, iconBg, title, message, secondaryLabel, onSecondary, primaryLabel, onPrimary, busy = false }) {
  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={busy ? undefined : onDismiss}
      contentClassName="sm:max-w-[440px] w-full rounded-xl bg-white overflow-hidden"
      zIndex={60}
    >
      <div className="px-6 pt-6 pb-5">
        <div className="flex items-start gap-4">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconBg}`}>{icon}</div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 leading-6">{title}</h3>
            {message && <p className="mt-2 text-sm text-gray-600 leading-relaxed">{message}</p>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4">
        {secondaryLabel && (
          <button
            type="button"
            onClick={onSecondary}
            disabled={busy}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {secondaryLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onPrimary}
          disabled={busy}
          className="rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0069d9] transition-colors disabled:opacity-60"
        >
          {busy ? 'Please wait…' : primaryLabel}
        </button>
      </div>
    </AnimatedModal>
  );
}

const REJECT_REASONS = ['Image is blurry or unreadable', 'Wrong document uploaded', 'Document is incomplete or cut off', 'Name does not match', 'Document has expired'];

/* ─────────── Reject control in the uploaded-document meta bar ─────────── */
function RejectControl({ rejection }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const { marked, busy, onReject, onUndo } = rejection;

  if (marked) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700">
          <XCircleIcon className="h-3.5 w-3.5" /> Rejected · not sent
        </span>
        <button
          type="button"
          onClick={onUndo}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> Undo
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 shadow-sm hover:bg-red-50 disabled:opacity-50 transition-colors"
      >
        <XCircleIcon className="h-3.5 w-3.5" /> Reject
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-80 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
          <p className="text-xs font-semibold text-slate-800">Reason <span className="font-normal text-slate-400">(optional, shown to the candidate)</span></p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {REJECT_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${reason === r ? 'border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                {r}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 300))}
            rows={2}
            placeholder="Add a note for the candidate…"
            className="mt-2 w-full resize-none rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-800 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setReason(''); }}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                if (await onReject(reason)) {
                  setOpen(false);
                  setReason('');
                }
              }}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            >
              Mark as rejected
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Nothing is sent yet. Mark all documents, then click “Send to candidate”.</p>
        </div>
      )}
    </div>
  );
}

/* ─────────── Fields editable by HR (Go Colors): ID number + name/DOB/gender, declarations ─────────── */
const wasEditedByHr = (uploaded, field = 'documentNumber') =>
  (uploaded?.hrEdits || []).some((e) => (e.changes || []).some((c) => c.field === field));

const ID_FIELD_LABELS = { name: 'Name on ID', dob: 'Date of birth', gender: 'Gender' };
const GENDER_OPTIONS = ['Male', 'Female', 'Transgender'];
const MARITAL_OPTIONS = ['Single', 'Married'];
// The ID read stores "20/03/2003"; a date input speaks "2003-03-20".
const dobToInput = (v) => {
  const m = String(v || '').match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

const inputClass =
  'rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]';

/** input: 'upper' (ID numbers), 'text', 'textarea', 'date', or an array of options (a select). */
function ValueEditor({ field, value, input, onSave, onCancel }) {
  const initial = input === 'date' ? dobToInput(value) : value || '';
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (draft.trim() === String(initial).trim()) {
      onCancel();
      return;
    }
    setSaving(true);
    const ok = await onSave({ [field]: draft.trim() });
    setSaving(false);
    if (ok) onCancel();
  };
  const common = { value: draft, autoFocus: true, required: true };
  let control;
  if (Array.isArray(input)) {
    control = (
      <select {...common} onChange={(e) => setDraft(e.target.value)} className={inputClass}>
        {!input.includes(draft) && <option value="">Select…</option>}
        {input.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (input === 'textarea') {
    control = (
      <textarea {...common} rows={3} maxLength={500} onChange={(e) => setDraft(e.target.value)} className={`${inputClass} w-full`} />
    );
  } else if (input === 'date') {
    control = (
      <input {...common} type="date" max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDraft(e.target.value)} className={inputClass} />
    );
  } else {
    control = (
      <input
        {...common}
        onChange={(e) => setDraft(input === 'upper' ? e.target.value.toUpperCase() : e.target.value)}
        maxLength={input === 'upper' ? 24 : 100}
        className={`${inputClass} w-52 ${input === 'upper' ? 'font-mono tracking-wide' : ''}`}
      />
    );
  }
  return (
    <form onSubmit={submit} className="mt-1 flex flex-wrap items-center gap-2" data-testid={`${field}-edit-form`}>
      {control}
      <button type="submit" disabled={saving} className="rounded-lg bg-[#007BFF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0062CC] disabled:opacity-60">
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
        Cancel
      </button>
    </form>
  );
}

/**
 * A stored value with copy, an "edited by HR" badge and, when `onSave` is given, an Edit control.
 * By default the document number — what the Excel export uses; `readFromDocument` is shown when
 * the stored number differs. `field`/`value`/`display`/`input` reuse it for any other field.
 */
function DocumentNumberField({
  label,
  uploaded,
  readFromDocument,
  onSave,
  mono = true,
  field = 'documentNumber',
  value: valueProp,
  display,
  input = 'upper',
  copyable = true,
}) {
  const [editing, setEditing] = useState(false);
  const value = String(valueProp ?? uploaded?.documentNumber ?? '');
  const shown = display ?? value;
  const read = String(readFromDocument || '').replace(/\s+/g, '').toUpperCase();
  return (
    <div>
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex flex-wrap items-center gap-1">
        {label}
        {wasEditedByHr(uploaded, field) && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold text-sky-800 normal-case tracking-normal">
            <PencilSquareIcon className="h-2.5 w-2.5" /> edited by HR
          </span>
        )}
      </span>
      {editing ? (
        <ValueEditor field={field} value={value} input={input} onSave={onSave} onCancel={() => setEditing(false)} />
      ) : (
        <div className="flex items-center gap-0.5 mt-0.5">
          <span className={`${mono ? 'font-mono tracking-wide' : ''} font-bold text-slate-900 text-sm select-all whitespace-pre-line break-words`}>{shown || '—'}</span>
          {value && copyable && <CopyButton value={value} />}
          {onSave && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              title="Edit"
              data-testid={field === 'documentNumber' ? 'document-number-edit-btn' : `${field}-edit-btn`}
              className="ml-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
            </button>
          )}
        </div>
      )}
      {!editing && read && value && read !== value.replace(/\s+/g, '').toUpperCase() && (
        <p className="mt-0.5 text-[11px] text-slate-500">Read from the document: <span className="font-mono">{readFromDocument}</span></p>
      )}
    </div>
  );
}

/* ─────────── Text declaration (address, marital status, UAN, no-PAN) ─────────── */
function declarationContent(r, uploaded) {
  const value = String(uploaded?.documentNumber || '').trim();
  if (uploaded?.documentNumberKind === 'pan_acknowledgement') {
    return { label: 'PAN acknowledgement number', value, note: 'Candidate has applied for a PAN and not received it yet.', mono: true };
  }
  if (r.subtype === 'uan_number') {
    if (value === 'NOT_APPLICABLE') {
      return { label: 'UAN number', value: 'No UAN allocated', note: 'Candidate declared they are a fresher or not an EPFO member.' };
    }
    return { label: 'UAN number', value, mono: true };
  }
  if (r.subtype === 'complete_address') return { label: 'Address', value };
  if (r.subtype === 'marital_status') return { label: 'Marital status', value };
  return { label: r.label, value };
}

function DeclarationCard({ r, uploaded, onSaveFields }) {
  const { label, value, note, mono } = declarationContent(r, uploaded);
  const copyable = value && value !== 'No UAN allocated';
  // Editable when the server sends an editKind: PAN acknowledgement, UAN, address, marital status.
  if (onSaveFields) {
    const raw = String(uploaded?.documentNumber || '');
    const input =
      r.subtype === 'complete_address' ? 'textarea' : r.subtype === 'marital_status' ? MARITAL_OPTIONS : 'upper';
    return (
      <div className="h-full overflow-y-auto px-5 py-5">
        <div className="max-w-2xl rounded-xl border border-slate-200 bg-slate-50/60 px-5 py-4">
          <DocumentNumberField
            label={label}
            uploaded={uploaded}
            onSave={onSaveFields}
            // "No UAN" is stored as NOT_APPLICABLE: shown in words, and the editor starts empty.
            value={raw === 'NOT_APPLICABLE' ? '' : raw}
            display={value}
            mono={!!mono}
            input={input}
            copyable={!!copyable}
          />
          {note && <p className="mt-1.5 text-xs text-slate-600">{note}</p>}
          <FieldEditHistory entries={uploaded?.hrEdits} labels={{ documentNumber: label }} />
        </div>
        <p className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] text-slate-400">
          <PencilSquareIcon className="h-3.5 w-3.5" />
          Typed by the candidate. No file was uploaded for this item.
        </p>
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto px-5 py-5">
      <div className="max-w-2xl rounded-xl border border-slate-200 bg-slate-50/60 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          {copyable && <CopyButton value={value} />}
        </div>
        <p
          className={`mt-1.5 whitespace-pre-line break-words text-slate-900 ${
            mono ? 'font-mono text-base font-semibold tracking-wider' : 'text-[15px] font-medium leading-relaxed'
          }`}
        >
          {value || <span className="font-normal text-slate-400">Not provided</span>}
        </p>
        {note && <p className="mt-1.5 text-xs text-slate-600">{note}</p>}
      </div>
      <p className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] text-slate-400">
        <PencilSquareIcon className="h-3.5 w-3.5" />
        Typed by the candidate. No file was uploaded for this item.
      </p>
    </div>
  );
}

/* ─────────── Uploaded document panel ─────────── */
function UploadedDocPanel({ tab, previewState, onDownload, downloading, getPreviewKind, onRetry, rejection, onSaveFields }) {
  const { r, uploaded } = tab;
  const { url, fileName, loading, error } = previewState;
  const previewKind = getPreviewKind(fileName, url || '');
  const idLabel = getIdLabel(r.label, r.type, uploaded?.documentNumber, uploaded?.documentNumberKind);
  const Icon = getDocIcon(r.label, r.type, uploaded?.documentNumber);

  const [mediaLoading, setMediaLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const isBank = r.type === 'bank_details' || r.subtype === 'bank_account';
  const historyEntries = isBank || isDeclarationOnly(r, uploaded) ? [] : [
    ...(uploaded?.verificationDetails?.candidateEdits
      ? [{ ...uploaded.verificationDetails.candidateEdits, byName: 'Candidate', byType: 'candidate' }]
      : []),
    ...(uploaded?.hrEdits || []),
  ];
  const canExpand =!!url && !loading && !error && (previewKind === 'image' || previewKind === 'pdf');

  useEffect(() => {
    if (url) {
      setMediaLoading(true);
    }
  }, [url]);

  return (
    <>
      {/* Meta bar */}
      <div className="shrink-0 border-b border-gray-100 px-5 py-3 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{r.label}</p>
              {uploaded?.uploadedAt && (
                <p className="text-[11px] text-slate-400">Received {formatDate(uploaded.uploadedAt)}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {uploaded?.id && !isDeclarationOnly(r, uploaded) && (
              <button
                type="button"
                onClick={() => onDownload(tab.key, uploaded.id, fileName, r.label)}
                disabled={downloading || loading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                {downloading ? 'Downloading…' : 'Download'}
              </button>
            )}
            {rejection && <RejectControl key={tab.key} rejection={rejection} />}
          </div>
        </div>
        {rejection?.marked?.reason && (
          <p className="mt-2 text-xs text-red-700">
            <span className="font-semibold">Reason:</span> {rejection.marked.reason}
          </p>
        )}

        {/* ── Document Details & AI Verification Box ── (declarations show theirs in the body) */}
        {isDeclarationOnly(r, uploaded) ? null : uploaded?.documentNumber === 'NOT_APPLICABLE' ? (
          <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
            <IdentificationIcon className="h-4 w-4 text-slate-500 shrink-0" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {idLabel}
              </p>
              <span className="text-xs font-semibold text-slate-700">
                Declared: No UAN Allocated (Fresher / Non-EPFO)
              </span>
            </div>
          </div>
        ) : (r.type === 'bank_details' || r.subtype === 'bank_account') && uploaded ? (
          <BankDetailsPanel uploaded={uploaded} CopyButton={CopyButton} onSave={onSaveFields || undefined} />
        ) : (() => {
          const vDet = uploaded?.verificationDetails;
          const vNorm = vDet?.normalizedData || vDet || {};
          const extName = vNorm?.name;
          // The stored number first: it is what the candidate confirmed (or HR corrected) and what
          // the Excel export uses. The OCR read is shown beside it only when the two differ.
          const extId = uploaded?.documentNumber || vNorm?.id_number;
          const extDob = vNorm?.dob;
          const extGender = vNorm?.gender;
          const docTypeDisplay = String(vDet?.docType || r.label).toUpperCase().replace(/_/g, ' ');
          const hasOcrDetails = !!(vDet && (extName || extDob || extGender || vDet?.isValidFormat || vDet?.isRecognizedId));

          if (hasOcrDetails) {
            return (
              <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3.5 space-y-2.5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-900">
                    <div className="p-1 rounded-lg bg-emerald-100 text-emerald-700">
                      <CheckCircleIcon className="h-4 w-4 shrink-0" />
                    </div>
                    <span>ID Authenticated ({docTypeDisplay})</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <SparklesIcon className="h-3 w-3 text-emerald-600" />
                    AI OCR Verified
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs pt-2 border-t border-emerald-100">
                  {onSaveFields ? (
                    <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                      <DocumentNumberField
                        label={ID_FIELD_LABELS.name}
                        uploaded={uploaded}
                        onSave={onSaveFields}
                        field="name"
                        value={extName || ''}
                        input="text"
                        mono={false}
                        copyable={false}
                      />
                    </div>
                  ) : extName && (
                    <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                        Name on ID
                      </span>
                      <span className="font-bold text-slate-900 text-sm mt-0.5 block truncate">
                        {extName}
                      </span>
                    </div>
                  )}
                  {uploaded?.documentNumber ? (
                    <div className={`bg-white/80 rounded-xl p-2.5 border border-emerald-100 ${onSaveFields ? 'sm:col-span-2' : ''}`}>
                      <DocumentNumberField
                        label={idLabel}
                        uploaded={uploaded}
                        readFromDocument={vNorm?.id_number}
                        onSave={onSaveFields}
                      />
                    </div>
                  ) : extId ? (
                    <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                        Extracted ID Number
                      </span>
                      <div className="flex items-center justify-between gap-1 mt-0.5">
                        <span className="font-mono font-bold text-slate-900 text-sm tracking-wide select-all">
                          {extId}
                        </span>
                        <CopyButton value={extId} />
                      </div>
                    </div>
                  ) : null}
                  {onSaveFields ? (
                    <>
                      <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                        <DocumentNumberField
                          label={ID_FIELD_LABELS.dob}
                          uploaded={uploaded}
                          onSave={onSaveFields}
                          field="dob"
                          value={extDob || ''}
                          input="date"
                          mono={false}
                          copyable={false}
                        />
                      </div>
                      {/* A PAN card prints no gender, so it is only offered where one can exist. */}
                      {(extGender || uploaded?.editKind !== 'pan') && (
                        <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                          <DocumentNumberField
                            label={ID_FIELD_LABELS.gender}
                            uploaded={uploaded}
                            onSave={onSaveFields}
                            field="gender"
                            value={extGender || ''}
                            input={GENDER_OPTIONS}
                            mono={false}
                            copyable={false}
                          />
                        </div>
                      )}
                    </>
                  ) : <>
                  {extDob && (
                    <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                        Date of Birth
                      </span>
                      <span className="font-semibold text-slate-900 text-sm mt-0.5 block">
                        {extDob}
                      </span>
                    </div>
                  )}
                  {extGender && (
                    <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                        Gender
                      </span>
                      <span className="font-semibold text-slate-900 text-sm mt-0.5 block">
                        {extGender}
                      </span>
                    </div>
                  )}
                  </>}
                  {(() => {
                    const summary = nameMatchSummary(vDet?.nameMatch);
                    if (!summary) return null;
                    const verified = summary.tone === 'match';
                    return (
                      <div className="bg-white/80 rounded-xl p-2.5 border border-emerald-100">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                          Profile Match
                        </span>
                        <span
                          className={`font-bold text-xs mt-0.5 inline-flex items-center gap-1 ${
                            verified ? 'text-emerald-700' : 'text-amber-700'
                          }`}
                        >
                          {verified ? '✅' : '⚠️'} {summary.recruiterText}
                        </span>
                        {vDet.nameMatch.comparedName && !verified && (
                          <span className="text-[11px] text-slate-600 mt-0.5 block">
                            Applied as “{vDet.nameMatch.comparedName}”
                          </span>
                        )}
                        {summary.reason && (
                          <span className="text-[11px] text-slate-500 mt-0.5 block">{summary.reason}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          }

          if (uploaded?.documentNumber) {
            return (
              <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#007BFF]/20 bg-[#007BFF]/5 px-4 py-2">
                <IdentificationIcon className="h-4 w-4 text-[#007BFF] shrink-0" />
                <DocumentNumberField label={idLabel} uploaded={uploaded} onSave={onSaveFields} />
              </div>
            );
          }

          return null;
        })()}
      </div>

      {/* Text-only declarations have no file: one compact card at the top, not an empty preview */}
      {isDeclarationOnly(r, uploaded) ? (
        <div className="min-h-0 flex-1 overflow-hidden bg-white">
          <DeclarationCard r={r} uploaded={uploaded} onSaveFields={onSaveFields} />
        </div>
      ) : (
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Edit history beside the file, so a growing history never shrinks the image.
            Bank details show their own history in the panel above. */}
        {historyEntries.length > 0 && (
          <div className="w-1/2 shrink-0 overflow-y-auto border-r border-gray-100 bg-white px-5 pb-4">
            {uploaded?.verificationDetails?.candidateEdits?.needsReview && (
              <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">
                ⚠️ The candidate changed the name or date of birth read from this card. Check it against the card image.
              </p>
            )}
            <FieldEditHistory entries={historyEntries} labels={{ documentNumber: idLabel, ...ID_FIELD_LABELS }} />
          </div>
        )}

      {/* Preview area */}
      <div className="relative min-h-[16rem] min-w-0 flex-1 overflow-hidden bg-gray-50">
        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="absolute right-3 top-3 z-20 rounded-lg border border-slate-200 bg-white/90 p-1.5 text-slate-600 shadow-sm hover:bg-white hover:text-slate-900"
            aria-label="Open large preview"
            title="Open large preview"
          >
            <ArrowsPointingOutIcon className="h-4 w-4" />
          </button>
        )}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
            <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-sm">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
              Loading preview…
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
              <XMarkIcon className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-sm font-medium text-slate-700">Preview failed to load</p>
            <p className="text-xs text-slate-500">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && url && previewKind === 'image' && (
          <div className="relative flex h-full w-full items-center justify-center p-4">
            {mediaLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-sm">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                  Loading document image…
                </div>
              </div>
            )}
            <img
              src={url}
              alt={r.label}
              onLoad={() => setMediaLoading(false)}
              onError={() => setMediaLoading(false)}
              className={`max-h-full max-w-full rounded-lg object-contain shadow-sm transition-opacity duration-300 ${mediaLoading ? 'opacity-0' : 'opacity-100'}`}
            />
          </div>
        )}

        {!loading && !error && url && previewKind === 'pdf' && (
          <div className="relative h-full w-full">
            {mediaLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-sm">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                  Loading PDF document…
                </div>
              </div>
            )}
            <object
              data={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              className="h-full w-full"
              style={{ border: 'none' }}
              key={url}
              onLoad={() => setMediaLoading(false)}
              onError={() => setMediaLoading(false)}
            >
              <iframe
                src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
                className="h-full w-full"
                title={r.label}
                style={{ border: 'none' }}
                onLoad={() => setMediaLoading(false)}
                onError={() => setMediaLoading(false)}
              />
            </object>
          </div>
        )}

        {!loading && !error && url && previewKind === 'unknown' && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <DocumentTextIcon className="h-12 w-12 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">Preview not available</p>
            <p className="text-xs text-slate-500">This file type cannot be previewed. Use the Download button to view it.</p>
          </div>
        )}

        {!loading && !error && !url && (
          <div className="flex h-full items-center justify-center">
            <div className="inline-flex items-center gap-2 text-sm text-slate-400">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
              Loading…
            </div>
          </div>
        )}
      </div>
      </div>
      )}
      {expanded && canExpand && (
        <DocumentLightbox url={url} kind={previewKind} title={r.label} onClose={() => setExpanded(false)} />
      )}
    </>
  );
}

/* Full-screen view of one document. Portalled: the modal's transform would otherwise trap `fixed`. */
function DocumentLightbox({ url, kind, title, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex flex-col bg-black/85" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex shrink-0 items-center justify-between px-5 py-3 text-white">
        <p className="text-sm font-semibold">{title}</p>
        <button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-white/10" aria-label="Close preview">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-5 pb-5" onClick={(e) => e.stopPropagation()}>
        {kind === 'image' ? (
          <img src={url} alt={title} className="max-h-full max-w-full rounded-lg object-contain" />
        ) : (
          <iframe src={url} title={title} className="h-full w-full max-w-5xl rounded-lg bg-white" style={{ border: 'none' }} />
        )}
      </div>
    </div>,
    document.body
  );
}

/* ─────────── Pending document panel ─────────── */
function PendingDocPanel({ tab, docStatus, candidate, reupload }) {
  const { r } = tab;
  const Icon = getDocIcon(r.label, r.type);

  if (reupload) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-400">
          <Icon className="h-8 w-8" />
        </div>
        <div>
          <p className="text-base font-semibold text-slate-800">{r.label}</p>
          <p className="mt-1.5 text-sm text-slate-500">
            Rejected{reupload.requestedAt ? ` on ${formatDate(reupload.requestedAt)}` : ''} and sent back to the candidate for re-upload.
          </p>
          {reupload.reason && (
            <p className="mt-1 text-xs text-slate-600"><span className="font-semibold">Reason:</span> {reupload.reason}</p>
          )}
        </div>
        <div className={`mt-2 inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 ${reupload.emailFailed ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
          {reupload.emailFailed ? (
            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 shrink-0" />
          ) : (
            <ClockIcon className="h-4 w-4 text-amber-500 shrink-0" />
          )}
          <span className={`text-xs ${reupload.emailFailed ? 'text-red-700' : 'text-amber-700'}`}>
            {reupload.emailFailed
              ? 'The email to the candidate could not be delivered. Use “Resend email” below.'
              : 'Awaiting re-upload. You will be emailed once the candidate uploads it.'}
          </span>
        </div>
      </div>
    );
  }

  const isSkipped = docStatus === 'skipped';
  const isCompleted = !!candidate?.completedAt || candidate?.status === 'Completed' || candidate?.status === 'Rejected' || candidate?.status === 'Selected';
  const isClosed = isSkipped || isCompleted;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
      <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${isClosed ? 'bg-slate-50 text-slate-400' : 'bg-amber-50 text-amber-400'}`}>
        <Icon className="h-8 w-8" />
      </div>
      <div>
        <p className="text-base font-semibold text-slate-800">{r.label}</p>
        <p className="mt-1.5 text-sm text-slate-500">
          This document was not uploaded.
        </p>
        {isClosed ? null : (
          <p className="mt-1 text-xs text-amber-600 font-medium">Awaiting candidate upload</p>
        )}
      </div>
      {isClosed ? null : (
        <div className="mt-2 inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
          <ClockIcon className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="text-xs text-amber-700">Candidate has been notified to upload this document.</span>
        </div>
      )}
    </div>
  );
}
